/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import {
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/CheckCircle';
import Breadcrumbs from 'components/Breadcrumbs';
import ButtonGroup from 'components/ButtonGroup';
import PageTitle from 'components/PageTitle';
import RefreshButton from 'components/RefreshButton';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import i18n from 'i18n';
import {
  CompanyData,
  PartnerData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { getAllClientsRequest } from 'modules/clients/actions';
import InputIcon from '@material-ui/icons/Input';
import DoneIcon from '@material-ui/icons/Done';
import SendIcon from '@material-ui/icons/Send';
import GetAppIcon from '@material-ui/icons/GetApp';
import {
  ClientFields,
  CloningStatusEnum,
  ListClientDataProps,
} from 'modules/clients/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { formatDateValue } from 'utils/dateFormat';
import { isClientOperator } from 'utils/role';
import { WarningOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import Button from 'components/Button';
import jsPDF from 'jspdf';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import FormCloneClient from './FormCloneClient';
import ClientSummary from '../ClientSummary/clientSummary';
import styles from './styles';
import {
  getClientNamePDF,
  getClientEnvironmentNamePDF,
  getClientLicensesPDF,
} from '../helpers';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

interface Props {
  clientList: ListClientDataProps;
  companySelected: CompanyData | null;
  isLoading: boolean;
  partnerSelected: PartnerData | null;
  permissions?: PermissionsData;
  role?: RolesEnum;
  getAllClientsRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
}

const ClientsList = ({
  clientList,
  companySelected,
  isLoading,
  partnerSelected,
  permissions,
  role,
  getAllClientsRequest,
  modalClose,
  modalOpen,
}: Props) => {
  const classes = useStyles();
  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);
  const history = useHistory();
  const [timeRequest, setTimeRequest] = React.useState(0);
  const [stopRequest, setStopRequest] = React.useState(true);
  const [refresh, setRefresh] = React.useState(false);
  const [headers] = React.useState(
    [
      {
        key: 'name',
        i18n: 'NAME_KEY',
        style: { width: 160, fontSize: 12 },
        order: true,
      },
      {
        key: 'identification',
        i18n: 'CLIENT_KEY',
        label: 'ID',
        style: { width: 40, fontSize: 12 },
        order: true,
      },
      {
        key: 'environment',
        i18n: 'ENVIRONMENT_KEY',
        style: {
          width: 160,
          fontSize: 12,
        },
        order: true,
      },
      {
        key: 'license_limit',
        i18n: 'LIMIT_LICENSES_KEY',
        style: { width: 30, fontSize: 12 },
        order: true,
        centerTitle: true,
        hidden: isClientOperator(role),
      },
      {
        key: 'sessions_active',
        i18n: 'ACTIVE_SESSIONS_KEY',
        style: { width: 30, fontSize: 12 },
        order: true,
        centerTitle: true,
      },
      {
        key: 'in_maintenance',
        i18n: 'STATUS_KEY',
        style: { width: 20, fontSize: 12 },
        order: true,
        centerTitle: true,
      },
      {
        key: 'user_last_access',
        i18n: 'LAST_ACCESS_SINGULAR_KEY',
        order: true,
        centerTitle: true,
        style: { width: 50, fontSize: 12 },
      },
      {
        key: 'actions',
        i18n: 'ACTIONS_KEY',
        style: { width: 15, fontSize: 12 },
        centerTitle: true,
      },
    ].filter(header => !header?.hidden)
  );

  const mapStatus = (status: CloningStatusEnum | string) => {
    const statusIconMap = {
      [CloningStatusEnum.requested]: <SendIcon className={classes.iconSize} />,
      [CloningStatusEnum.finished]: <DoneIcon className={classes.iconSize} />,
      [CloningStatusEnum.started]: (
        <CircularProgress size={12} className={classes.iconSize} />
      ),
    };

    return statusIconMap[status] || <DoneIcon className={classes.iconSize} />;
  };

  const mapStatusName = (status: CloningStatusEnum | string) => {
    const statusNameMap = {
      [CloningStatusEnum.requested]: i18n.t('STATUS_REQUESTED_KEY'),
      [CloningStatusEnum.finished]: i18n.t('STATUS_FINISHED_KEY'),
      [CloningStatusEnum.started]: i18n.t('STATUS_STARTED_KEY'),
    };

    return statusNameMap[status] || i18n.t('PERMANENT_KEY');
  };

  const intervalRef = React.useRef<any>();

  const getClients = (refresh = false) => {
    if (
      (companySelected && partnerSelected) ||
      permissions?.can_view_all_resources
    ) {
      getAllClientsRequest({
        ...query,
        company_uuid: permissions?.can_view_all_resources
          ? ''
          : companySelected?.id,
        partner_uuid: permissions?.can_view_all_resources
          ? ''
          : partnerSelected?.id,
      });

      setRefresh(refresh);
    }
  };

  React.useEffect(() => {
    getClients();
  }, [
    getAllClientsRequest,
    query,
    companySelected,
    partnerSelected,
    timeRequest,
  ]);

  React.useEffect(() => {
    const stop = clientList.results.find(
      item =>
        item?.clone_status === 'requested' || item?.clone_status === 'started'
    );

    if (stop === undefined) {
      setStopRequest(true);
    } else {
      setStopRequest(false);
    }
  }, [clientList]);

  React.useEffect(() => {
    if (stopRequest) {
      clearInterval(intervalRef.current);
    } else {
      intervalRef.current = setInterval(() => {
        setTimeRequest(props => props + 1);
      }, 5000);
    }
  }, [stopRequest]);

  const handleClick = (item: ClientFields) => {
    if (!!permissions && permissions.edit_client) {
      history.push(`/clients/${item?.client_uuid}/configurations`);
    }
  };

  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ format: 'dl', orientation: 'landscape' });

  const generatePDFButton = (client: ClientFields | null) => {
    doc.setFontSize(19);
    doc.text(
      [
        '',
        `${i18n.t('CLIENT_DATA_KEY')}:`,
        '',
        getClientNamePDF(client),
        getClientEnvironmentNamePDF(client),
        !isClientOperator(role) ? getClientLicensesPDF(client) : '',
      ],
      10,
      10,
      { lineHeightFactor: 1.5, maxWidth: 200 }
    );
    doc.save(`${client?.name}.pdf`);
  };

  const handleOpenClientSummary = (client: ClientFields) => {
    modalOpen({
      title: i18n.t('CLIENT_DATA_KEY'),
      content: <ClientSummary client={client} />,
      footer: (
        <Grid>
          <Button
            id="generate-pdf-button"
            submitButton
            startIcon={<GetAppIcon />}
            onClick={() => {
              generatePDFButton(client);
              modalClose();
              setRefresh(true);
            }}
          >
            {i18n.t('GENERATE_PDF')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
      >
        <PageTitle
          title={i18n.t('CLIENTS_PAGE.LIST_CLIENTS_TITLE_KEY')}
          subtitle={
            <Breadcrumbs
              values={[
                {
                  name: i18n.t('ENVIRONMENTS_KEY'),
                  link: '/environments',
                },
                {
                  name: i18n.t('CLIENTS_KEY'),
                },
              ]}
            />
          }
        />

        <Grid
          container
          direction="row"
          alignItems="center"
          justify="flex-end"
          xs={3}
        >
          <RefreshButton
            id="btn-refresh"
            onClick={() => getClients(true)}
            animate={isLoading}
            disabled={isLoading}
          />

          <Permission value={'create_new_client'}>
            <ButtonGroup
              style={{ marginLeft: 16 }}
              label={i18n.t('GENERAL_ACTIONS_KEY')}
              options={[
                {
                  label: i18n.t('ADD_CLIENT_KEY'),
                  onClick: () => history.push('/clients/new'),
                  hidden: !!permissions && !permissions.create_new_client,
                },
              ]}
            />
          </Permission>
        </Grid>
      </Grid>

      <Grid container>
        <SearchBar
          id="search-clients"
          isLoading={isLoading}
          searchValue={searchValue}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={
            <>
              <Permission value="edit_client">
                <TableCell className={classes.accessButtonHeader} centerTitle>
                  {i18n.t('ACCESS_KEY')}
                </TableCell>
              </Permission>
              {headers.map(item => (
                <TableCell
                  className={classes.header}
                  centerTitle={item?.centerTitle}
                  textAlignCenter
                  key={item?.key}
                  order={item?.order}
                  markOderItem={itemOrder === item?.key}
                  style={item?.style || {}}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                >
                  {`${i18n.t(item?.i18n)} ${item?.label ? item?.label : ''}`}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: ClientFields, index) => (
            <TableRow
              key={item?.client_uuid}
              className={clsx(classes.summaryRowTable, {
                [classes.envRow]: !!item?.in_maintenance,
                [classes.envRowDirect]:
                  !!permissions && permissions.edit_client,
              })}
            >
              <Permission value="edit_client">
                <TableCell
                  ellipsis
                  centerTitle
                  className={classes.centerFields}
                  onClick={() => {
                    handleClick(item);
                  }}
                >
                  <IconButton
                    onClick={() => {
                      handleClick(item);
                    }}
                  >
                    <InputIcon className={classes.accessButton} />
                  </IconButton>
                </TableCell>
              </Permission>
              <TableCell
                onClick={() => {
                  handleClick(item);
                }}
                tooltip={item?.name}
                ellipsis
                maxLength={19}
                className={classes.longNameField}
              >
                <Typography className={classes.longNameField}>
                  {item?.name}
                </Typography>
              </TableCell>
              <TableCell
                onClick={() => {
                  handleClick(item);
                }}
              >
                {item?.identification || (
                  <CircularProgress size={16} color="secondary" />
                )}
              </TableCell>
              <TableCell
                onClick={() => {
                  handleClick(item);
                }}
                tooltip={item?.environment.name}
                ellipsis
                copyable
                className={classes.longField}
              >
                <Typography className={classes.longField}>
                  {item?.environment.name}
                </Typography>
              </TableCell>
              {!isClientOperator(role) && (
                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  align="center"
                >
                  {item?.licenses?.license_limit}
                </TableCell>
              )}
              <TableCell
                onClick={() => {
                  handleClick(item);
                }}
                align="center"
              >
                {item?.metrics.sessions.active}
              </TableCell>
              <TableCell
                onClick={() => {
                  handleClick(item);
                }}
                className={classes.iconContainer}
                centerTitle
              >
                {item?.in_maintenance ? (
                  <Tooltip
                    placement="top"
                    arrow
                    title={i18n.t('CLIENT_MAINTENANCE_WARNING_KEY')}
                  >
                    <WarningOutlined
                      color="error"
                      className={classes.inMaintenance}
                    >
                      {i18n.t('CLIENT_MAINTENANCE_WARNING_KEY')}
                    </WarningOutlined>
                  </Tooltip>
                ) : (
                  <Tooltip
                    placement="top"
                    arrow
                    title={mapStatusName(item?.clone_status)}
                  >
                    <CheckIcon
                      className={classes[item?.clone_status || 'finished']}
                    >
                      {mapStatus(item?.clone_status)}
                    </CheckIcon>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell
                onClick={() => {
                  handleClick(item);
                }}
                centerTitle
                textAlignCenter
                className={classes.dateField}
              >
                {formatDateValue(item?.user_last_access)}
              </TableCell>
              <TableCell className={classes.centralize}>
                <MenuActions id={`btn-options-clients-${index}`}>
                  <Permission value="clone_client">
                    <MenuActionItem
                      id="btn-clone-client"
                      onClick={() =>
                        modalOpen({
                          title: i18n.t('CLIENTS_PAGE.CLONE_CLIENT_KEY'),
                          content: (
                            <FormCloneClient
                              clientUuid={item?.client_uuid}
                              clientCopyName={item?.name}
                              onCloseModal={modalClose}
                              onCancel={modalClose}
                            />
                          ),
                          noBorder: true,
                          maxWidth: 'sm',
                        })
                      }
                    >
                      {i18n.t('CLONE_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission value="edit_client">
                    <MenuActionItem
                      id="btn-edit-client"
                      component={Link}
                      to={`/clients/${item?.client_uuid}/configurations`}
                    >
                      {i18n.t('EDIT_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission value="list_users">
                    <MenuActionItem
                      id="btn-users-client"
                      component={Link}
                      to={`/clients/${item?.client_uuid}/users`}
                    >
                      {i18n.t('USERS_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission
                    value={
                      item?.environment?.client_apps?.registration_source ===
                      'platform'
                        ? 'list_included_applications'
                        : 'list_applications'
                    }
                  >
                    <MenuActionItem
                      id="btn-applications-client"
                      component={Link}
                      to={`/clients/${item?.client_uuid}/applications`}
                    >
                      {i18n.t('APPLICATIONS_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission
                    value={[
                      'list_environment_instance_sessions',
                      'list_client_instance_sessions',
                      'list_session_history',
                    ]}
                  >
                    <MenuActionItem
                      id="btn-sessions-client"
                      component={Link}
                      to={`/clients/${item?.client_uuid}/sessions`}
                    >
                      {i18n.t('SESSIONS_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission value="client_access_control">
                    <MenuActionItem
                      id="btn-restrictions-client"
                      component={Link}
                      to={`/clients/${item?.client_uuid}/access-restrictions`}
                    >
                      {i18n.t('RESTRICTIONS_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission
                    value={[
                      'get_client_maintenance',
                      'list_client_scheduled_warnings',
                    ]}
                  >
                    <MenuActionItem
                      id="btn-maintenance-client"
                      component={Link}
                      to={`/clients/${item?.client_uuid}/maintenance`}
                    >
                      {i18n.t('MAINTENANCE_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <MenuActionItem
                    id="btn-client-summary"
                    onClick={() => handleOpenClientSummary(item)}
                  >
                    {i18n.t('CLIENT_DATA_SUMMARY_KEY')}
                  </MenuActionItem>
                  <Permission value="list_all_operators_audit_logs">
                    <MenuActionItem
                      id="btn-audit"
                      component={Link}
                      to={`/clients/${item?.client_uuid}/audit`}
                    >
                      {i18n.t('LOGS_PAGE.LIST_LOGS_KEY')}
                    </MenuActionItem>
                  </Permission>
                </MenuActions>
              </TableCell>
            </TableRow>
          )}
          data={clientList}
          isLoading={!refresh && stopRequest && isLoading}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('CLIENTS_PAGE.NO_CLIENTS_KEY')}
          alertText={
            (!companySelected || !partnerSelected) &&
            !permissions?.can_view_all_resources
              ? i18n.t('TO_LIST_SELECT_COMPANY_AND_PARTNER_KEY')
              : null
          }
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, partnerSelected, data },
  clients: { isLoading, clientList },
}: ApplicationState) => ({
  companySelected,
  partnerSelected,
  isLoading,
  clientList,
  permissions: data?.permissions,
  role: data?.role,
});

export default connect(mapStateToProps, {
  getAllClientsRequest,
  modalOpen,
  modalClose,
})(ClientsList);
